import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/layout/Header";
import { CheckCircle, XCircle, Clock, Building2 } from "lucide-react";

interface CompanyRequest {
  id: string;
  company_id: string | null;
  status: string;
  requested_at: string;
  company_name: string;
  company_sector: string;
  company_employee_count: number;
  requester: {
    id: string;
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

const AdminDashboard = () => {
  const [requests, setRequests] = useState<CompanyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [showRejectionModal, setShowRejectionModal] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkUserRole();
    fetchRequests();
  }, []);

  const checkUserRole = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/auth');
      return;
    }

    // Check role from user_roles table
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .single();

    if (!roleData) {
      navigate('/');
      return;
    }
  };

  const fetchRequests = async () => {
    try {
      // First get the company requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('company_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (requestsError) throw requestsError;

      // Then get the profile data for each request
      const requestsWithRequester = await Promise.all(
        (requestsData || []).map(async (request) => {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, user_id, first_name, last_name, email')
            .eq('id', request.requester_id)
            .single();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
          }

          return {
            ...request,
            requester: profile
          };
        })
      );

      setRequests(requestsWithRequester);
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les demandes"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      // Find the request to get company details and requester info
      const request = requests.find(r => r.id === requestId);
      if (!request || !request.requester) throw new Error("Request or requester not found");

      // Get current admin profile ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: adminProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!adminProfile) throw new Error("Admin profile not found");

      // Create the company
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: request.company_name,
          sector: request.company_sector,
          employee_count: request.company_employee_count,
          admin_id: request.requester.id
        })
        .select()
        .single();

      if (companyError) throw companyError;

      // Update the company request with the new company_id and approved status
      const { error: requestError } = await supabase
        .from('company_requests')
        .update({
          company_id: companyData.id,
          status: 'approved',
          processed_at: new Date().toISOString(),
          processed_by: adminProfile.id
        })
        .eq('id', requestId);

      if (requestError) throw requestError;

      // Update the user's company_id in profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ company_id: companyData.id })
        .eq('id', request.requester.id);

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw new Error(`Failed to update user profile: ${profileError.message}`);
      }

      // Add company role to user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: request.requester.user_id,
          role: 'company'
        });

      if (roleError && roleError.code !== '23505') { // Ignore duplicate key errors
        console.error('Error adding company role:', roleError);
        throw new Error(`Failed to assign company role: ${roleError.message}`);
      }

      // Send approval email
      await supabase.functions.invoke('send-company-decision-emails', {
        body: {
          action: 'approved',
          companyName: request.company_name,
          companyEmail: request.requester.email,
          contactFirstName: request.requester.first_name,
          contactLastName: request.requester.last_name
        }
      });

      toast({
        title: "Demande approuvée",
        description: "L'entreprise peut maintenant se connecter et a reçu un email de confirmation"
      });

      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'approuver la demande"
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!rejectionReason.trim()) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez indiquer la raison du refus"
      });
      return;
    }

    setProcessingId(requestId);
    try {
      // Find the request to get company details and requester info
      const request = requests.find(r => r.id === requestId);
      if (!request || !request.requester) throw new Error("Request or requester not found");

      const { error } = await supabase
        .from('company_requests')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
          processed_at: new Date().toISOString(),
          processed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', requestId);

      if (error) throw error;

      // Send rejection email
      await supabase.functions.invoke('send-company-decision-emails', {
        body: {
          action: 'rejected',
          companyName: request.company_name,
          companyEmail: request.requester.email,
          contactFirstName: request.requester.first_name,
          contactLastName: request.requester.last_name,
          rejectionReason: rejectionReason
        }
      });

      toast({
        title: "Demande refusée",
        description: "L'entreprise a été notifiée du refus par email"
      });

      setShowRejectionModal(null);
      setRejectionReason("");
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de refuser la demande"
      });
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Refusée</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        <Header showLogoutButton={true} />
        <div className="container mx-auto py-16 px-4">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Header showLogoutButton={true} />
      <div className="container mx-auto py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard Administrateur</h1>
                <p className="text-muted-foreground">Gérez les demandes de création de comptes entreprise</p>
              </div>
              <Button asChild>
                <Link to="/admin-destinations">
                  Gérer les destinations
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid gap-6">
            {requests.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucune demande de création d'entreprise</p>
                </CardContent>
              </Card>
            ) : (
              requests.map((request) => (
                <Card key={request.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{request.company_name}</CardTitle>
                        <CardDescription>
                          Demandé par {request.requester?.first_name || 'N/A'} {request.requester?.last_name || 'N/A'} ({request.requester?.email || 'N/A'})
                        </CardDescription>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Secteur</p>
                        <p className="font-medium">{request.company_sector}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Nombre d'employés</p>
                        <p className="font-medium">{request.company_employee_count}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Date de demande</p>
                        <p className="font-medium">
                          {new Date(request.requested_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {request.status === 'pending' && (
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleApprove(request.id)}
                          disabled={processingId === request.id}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approuver
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => setShowRejectionModal(request.id)}
                          disabled={processingId === request.id}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    )}

                    {showRejectionModal === request.id && (
                      <div className="mt-4 p-4 border rounded-lg bg-background">
                        <h4 className="font-medium mb-2">Raison du refus</h4>
                        <Textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Expliquez pourquoi cette demande est refusée..."
                          className="mb-3"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(request.id)}
                            disabled={processingId === request.id}
                          >
                            Confirmer le refus
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowRejectionModal(null);
                              setRejectionReason("");
                            }}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;